import { useState } from "react";
import { captureLead } from "@/lib/api";
import { ArrowRight, Mail, Loader2, Check, X, Sparkles } from "lucide-react";

interface Props {
  /** What the tool is about, shown in the modal */
  toolName: string;
  /** Called when user submits email or skips — parent should then show results */
  onComplete: () => void;
  /** Called to close the modal without proceeding */
  onClose: () => void;
  /** The page path for lead source tracking */
  source: string;
}

/**
 * EmailGateModal — captures an email before showing tool results.
 * Shows a friendly "we'll email you the results" prompt.
 * Users can skip but we make the email path more prominent.
 */
export default function EmailGateModal({ toolName, onComplete, onClose, source }: Props) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;

    setStatus("loading");
    try {
      await captureLead(email.trim(), source);
      setStatus("success");
      setTimeout(() => onComplete(), 1200);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
    }
  }

  if (status === "success") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-in">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check size={24} className="text-emerald-600" />
          </div>
          <h3 className="font-display font-bold text-ink text-lg">You're in!</h3>
          <p className="text-sm text-slate mt-1">Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-br from-ink to-[#1a2830] px-6 py-8 text-center relative">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors">
            <X size={18} />
          </button>
          <div className="w-12 h-12 rounded-xl bg-coral/20 flex items-center justify-center mx-auto mb-3">
            <Sparkles size={22} className="text-coral" />
          </div>
          <h2 className="font-display font-bold text-white text-xl">Your Results Are Ready</h2>
          <p className="text-white/50 text-sm mt-2">
            Enter your email and we'll send you a copy of your {toolName} results — plus tips to improve.
          </p>
        </div>

        {/* Form */}
        <div className="px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate/40" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoFocus
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-sand text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all"
                disabled={status === "loading"}
              />
            </div>

            <button type="submit" disabled={!email.trim() || status === "loading"}
              className="w-full flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 shadow-md shadow-coral/20">
              {status === "loading" ? (
                <><Loader2 size={15} className="animate-spin" /> Sending...</>
              ) : (
                <><Mail size={15} /> Email Me My Results</>
              )}
            </button>

            {status === "error" && (
              <p className="text-red-500 text-xs text-center">Something went wrong. Try again.</p>
            )}
          </form>

          {/* Skip option — smaller and less prominent */}
          <div className="mt-4 text-center">
            <button onClick={onComplete}
              className="text-xs text-slate/40 hover:text-slate/60 transition-colors">
              Skip — just show my results
            </button>
          </div>

          <p className="text-[10px] text-slate/30 text-center mt-3">
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
